"use client";
import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";

const cls = (...c) => c.filter(Boolean).join(" ");
const fmtC = (n) => {
  if (!Number.isFinite(n)) return "$0.00";
  const abs = Math.abs(n);
  const s = abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `($${s})` : `$${s}`;
};

function computeCovFromSize(size, uom) {
  if (!size || !isCovUom(uom)) return null;
  const s = String(size).trim();
  const parseToFeet = (str) => {
    const t = String(str).trim();
    const parseFrac = (f) => {
      const p = f.split("/");
      return p.length === 2
        ? parseFloat(p[0]) / parseFloat(p[1])
        : parseFloat(f) || 0;
    };
    const m1 = t.match(/^([\d.]+)'\s*([\d]+)[- ]([\d]+\/[\d]+)"?$/);
    if (m1)
      return parseFloat(m1[1]) + (parseFloat(m1[2]) + parseFrac(m1[3])) / 12;
    const m2 = t.match(/^([\d.]+)'([\d.]+)"?$/);
    if (m2) return parseFloat(m2[1]) + (parseFloat(m2[2]) || 0) / 12;
    if (t.endsWith("'")) return parseFloat(t) || 0;
    if (t.endsWith('"')) return (parseFloat(t) || 0) / 12;
    return parseFloat(t) || 0;
  };
  const ul = (uom || "").toLowerCase();
  if (ul.includes("sf") || ul.includes("sq") || ul.includes("sheet")) {
    const parts = s.replace(/×/g, "x").split(/\s*[xX]\s*/);
    if (parts.length === 2) {
      const w = parseToFeet(parts[0]);
      const h = parseToFeet(parts[1]);
      if (w > 0 && h > 0) return parseFloat((w * h).toFixed(4));
    }
  } else if (ul.includes("lf") || ul.includes("lin")) {
    const len = parseToFeet(s);
    if (len > 0) return parseFloat(len.toFixed(4));
  }
  return null;
}

const SF_LF = ["sf", "lf", "sq ft", "lin ft", "square", "linear", "sheet"];
const isCovUom = (uom) =>
  SF_LF.some((u) => (uom || "").toLowerCase().includes(u));

const TAB_DB = "db";
const TAB_CSV = "csv";
const TAB_TANDM = "tandm";

// ─────────────────────────────────────────────────────────────────────────────
// Search engine — upgraded to match ItemSearchHelper3
// ─────────────────────────────────────────────────────────────────────────────

const ALIASES = {
  "1/2": ["half", '1/2"', "half-inch", "0.5"],
  "5/8": ['5/8"', "five-eighth", "0.625"],
  "3/4": ['3/4"', "0.75", "three-quarter"],
  "1-5/8": ['1-5/8"', "1 5/8"],
  "3-5/8": ['3-5/8"', "3 5/8"],
  "2-1/2": ['2-1/2"', "2 1/2"],
  "25ga": ["25 ga", "25gauge", "25g"],
  "20ga": ["20 ga", "20gauge"],
  "18ga": ["18 ga", "18gauge"],
  "16ga": ["16 ga", "16gauge"],
  "14ga": ["14 ga", "14gauge"],
  dw: ["drywall", "dry wall"],
  gb: ["gypsum board", "gyp board"],
  mtl: ["metal"],
  std: ["stud", "studs"],
  ins: ["insulation"],
  trk: ["track"],
  cgc: ["cgc"],
  ussg: ["usg", "uss"],
};

// Extract fraction-like patterns as atomic units before any splitting
const FRACTION_RE = /\d+[-–]\d+\/\d+|\d+\/\d+/g;

function normaliseFractions(str) {
  return (str || "").replace(FRACTION_RE, (m) => m.replace(/[\s\-\/]/g, "_"));
}

function normalise(str) {
  return normaliseFractions(str || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenise(str) {
  return normalise(str)
    .split(/[\s\-\/,]+/)
    .filter(Boolean);
}

// ── Product type compounds ─────────────────────────────────────────────────
const PRODUCT_COMPOUNDS = [
  ["z", "bar"],
  ["z", "furring"],
  ["hat", "channel"],
  ["resilient", "channel"],
  ["rc", "channel"],
  ["corner", "bead"],
  ["l", "bead"],
  ["j", "bead"],
  ["j", "trim"],
  ["cee", "stud"],
  ["drywall", "screw"],
  ["type", "s"],
  ["type", "x"],
  ["tek", "screw"],
  ["lay", "in"],
  ["drop", "ceiling"],
];

function extractCompounds(str) {
  const toks = tokenise(str);
  const found = new Set();
  for (const [a, b] of PRODUCT_COMPOUNDS) {
    for (let i = 0; i < toks.length - 1; i++) {
      if (toks[i] === a && toks[i + 1] === b) {
        found.add(`${a} ${b}`);
      }
    }
    if (toks.includes(`${a}${b}`)) found.add(`${a} ${b}`);
  }
  return found;
}

// ── Dimensional phrase extraction ──────────────────────────────────────────
const DIM_DESCRIPTORS =
  "leg|stud|track|flange|web|wing|return|lip|face|bar|channel|bead";

const DIM_PHRASE_PATTERNS = [
  new RegExp(
    `(\\d+(?:[-–]\\d+\\/\\d+)?(?:\\/\\d+)?)["']?\\s*(${DIM_DESCRIPTORS})`,
    "gi",
  ),
  /(\d+)\s*(ga|gauge|mil)\b/gi,
  /(\d+\/\d+)["']/gi,
];

function extractDimPhrases(str) {
  const norm = normalise(str);
  const phrases = new Set();
  for (const re of DIM_PHRASE_PATTERNS) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(norm)) !== null) {
      const num = normaliseFractions(m[1])
        .replace(/[\s\-\/]/g, "_")
        .toLowerCase();
      const desc = (m[2] || "").toLowerCase();
      const d = desc === "gauge" ? "ga" : desc === "mil" ? "mil" : desc;
      phrases.add(d ? `${num} ${d}` : num);
    }
  }
  return phrases;
}

// ── AI intent infrastructure (mirrors ItemSearchHelper3) ──────────────────
const API_BASE = "https://erp-xe9u.onrender.com/api";
const intentCache = new Map();

const AI_TRIGGER_RE = [
  /(?<![a-zA-Z])\b(158|162|212|250|312|358|362|400|412|600|800|1000|1200)\b(?!\s*['"])/,
  /\b(\d+\/\d+|\d{3,})\s*(flange|leg|\bfl\b)/i,
  /\b\d+\s*mil\b/i,
];

function queryNeedsAI(query) {
  return AI_TRIGGER_RE.some((re) => re.test(query));
}

async function extractSearchIntent(rawQuery, token) {
  const key = rawQuery.trim().toLowerCase();
  if (intentCache.has(key)) return intentCache.get(key);
  if (key.length < 3) return null;
  if (!queryNeedsAI(rawQuery)) return null;

  try {
    const res = await fetch(`${API_BASE}/aiSearch/parse-search-intent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ query: rawQuery.slice(0, 150) }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.success) return null;
    intentCache.set(key, data.intent);
    return data.intent;
  } catch {
    return null;
  }
}

// ── Full scoring engine ────────────────────────────────────────────────────
function scoreItem(item, queryTokens, rawQuery = "", intent = null) {
  if (!queryTokens.length) return { score: 0, matched: [] };

  const uniqueQueryTokens = [...new Set(queryTokens.map(normalise))].filter(
    Boolean,
  );

  const fields = {
    itemCode: normalise(item.itemCode || item.matCode || ""),
    description: normalise(
      item.description ||
        item.costCodeDescription ||
        item.itemDescription ||
        "",
    ),
    size: normalise(item.size || ""),
    section: normalise(item.sectionNumber || ""),
    rest: normalise(
      [item.bidArea, item.uom, item.category, item.costCodeDescription]
        .filter(Boolean)
        .join(" "),
    ),
  };

  const descTokens = tokenise(fields.description);
  const allHaystackTokens = tokenise(Object.values(fields).join(" "));
  const allHaystack = Object.values(fields).join(" ");

  let score = 0;
  const matched = [];
  const unmatched = [];

  // ── Intent-based structured scoring ───────────────────────────────────────
  if (intent) {
    const intentIsReliable =
      intent.productType ||
      intent.gauge ||
      (intent.webSize && /[-\/]/.test(intent.webSize));

    if (intentIsReliable) {
      if (intent.webSize) {
        const normSize = normalise(intent.webSize);
        const sizeTokens = tokenise(intent.webSize);
        const hit =
          allHaystack.includes(normSize) ||
          sizeTokens.every((t) => allHaystackTokens.includes(t));
        if (hit) {
          score += 120;
          matched.push(`size:${intent.webSize}`);
        } else {
          const hasSomeSize = /\d[_\-\/]\d/.test(allHaystack);
          if (hasSomeSize) score -= 80;
        }
      }

      if (intent.productType) {
        const TYPE_KEYWORDS = {
          stud: ["stud", "studs"],
          track: ["track", "tracks"],
          "slotted track": ["slotted", "sdt"],
          "z bar": ["z-bar", "z bar", "zb"],
          "furring channel": ["furring", "fch"],
          "resilient channel": ["resilient", "rch"],
          "flat stock": ["flat stock", "fs"],
          angle: ["angle"],
          "flex track": ["flex", "flexible"],
          "j runner": ["j-runner", "j runner", "jr"],
          "ch stud": ["ch stud", "shaftwall"],
          "carrying channel": ["carrying channel", "cch"],
          "hanger wire": ["hanger wire", "hw"],
          screw: ["screw"],
          insulation: ["insulation", "rockwool", "quietzone"],
          drywall: ["drywall", "gypsum"],
        };
        const keywords = TYPE_KEYWORDS[intent.productType] || [
          intent.productType,
        ];
        const hit = keywords.some((kw) => allHaystack.includes(normalise(kw)));
        if (hit) {
          score += 100;
          matched.push(`type:${intent.productType}`);
        } else {
          score -= 70;
        }
      }

      if (intent.flange) {
        const normFlange = normalise(intent.flange);
        const flangeTokens = tokenise(intent.flange);
        const hit =
          allHaystack.includes(normFlange) ||
          flangeTokens.every((t) => allHaystackTokens.includes(t));
        if (hit) {
          score += 100;
          matched.push(`flange:${intent.flange}`);
        } else {
          const hasFlangeRef =
            allHaystack.includes("flange") || allHaystack.includes("leg");
          if (hasFlangeRef) score -= 60;
        }
      }

      if (intent.gauge) {
        const GAUGE_SIGNALS = {
          25: ["-18", "25ga", "25 ga"],
          20: ["-33", "20ga", "20 ga"],
          18: ["-43", "18ga", "18 ga"],
          16: ["-54", "16ga", "16 ga"],
          14: ["-68", "14ga", "14 ga"],
          12: ["-97", "12ga", "12 ga"],
        };
        const signals = GAUGE_SIGNALS[intent.gauge] || [`${intent.gauge}ga`];
        const hit =
          signals.some((s) => allHaystack.includes(normalise(s))) ||
          signals.some((s) => fields.itemCode.includes(normalise(s)));
        if (hit) {
          score += 100;
          matched.push(`gauge:${intent.gauge}ga`);
        } else {
          const hasGaugeRef =
            /\d{2}ga/.test(allHaystack) || /\-\d{2}$/.test(fields.itemCode);
          if (hasGaugeRef) score -= 60;
        }
      }

      const specifiedFields = [
        intent.webSize ? "size:" : null,
        intent.productType ? "type:" : null,
        intent.flange ? "flange:" : null,
        intent.gauge ? "gauge:" : null,
      ].filter(Boolean);

      const allIntentFieldsMatched =
        specifiedFields.length > 0 &&
        specifiedFields.every((prefix) =>
          matched.some((m) => m.startsWith(prefix)),
        );
      if (allIntentFieldsMatched) score += 50;
    }
  }

  // ── Compound product-type matching ─────────────────────────────────────────
  const queryCompounds = extractCompounds(rawQuery);
  const descCompounds = extractCompounds(
    item.description || item.costCodeDescription || item.itemDescription || "",
  );

  let compoundScore = 0;
  const matchedCompoundTokens = new Set();

  for (const qc of queryCompounds) {
    if (descCompounds.has(qc)) {
      compoundScore += 90;
      qc.split(" ").forEach((t) => matchedCompoundTokens.add(t));
    } else {
      compoundScore -= 40;
    }
  }

  // ── Dimensional phrase matching ────────────────────────────────────────────
  const queryDimPhrases = extractDimPhrases(rawQuery);
  const descDimPhrases = extractDimPhrases(
    item.description || item.costCodeDescription || item.itemDescription || "",
  );

  let dimScore = 0;
  const queryDescriptors = new Set();
  const queryDescriptorValues = new Map();

  for (const qp of queryDimPhrases) {
    const parts = qp.includes(" ") ? qp.split(" ") : [qp, ""];
    const val = parts[0];
    const desc = parts[1];
    if (desc) {
      queryDescriptors.add(desc);
      queryDescriptorValues.set(desc, val);
    }
    if (descDimPhrases.has(qp)) dimScore += 80;
  }

  for (const desc of queryDescriptors) {
    const qVal = queryDescriptorValues.get(desc);
    const candidateHasSameDescriptor = [...descDimPhrases].some((p) =>
      p.endsWith(" " + desc),
    );
    const candidateMatchesValue = descDimPhrases.has(`${qVal} ${desc}`);
    if (candidateHasSameDescriptor && !candidateMatchesValue) dimScore -= 60;
  }

  score += dimScore + compoundScore;

  // ── Token-level fuzzy scoring ──────────────────────────────────────────────
  for (const qtNorm of uniqueQueryTokens) {
    if (matchedCompoundTokens.has(qtNorm)) {
      matched.push(qtNorm);
      continue;
    }

    let hitScore = 0;
    const isShortNumeric = /^\d{1,2}$/.test(qtNorm);
    const isFractionToken = /_/.test(qtNorm);

    if (fields.itemCode === qtNorm) hitScore = 100;
    else if (
      !isShortNumeric &&
      qtNorm.length >= 3 &&
      fields.itemCode.startsWith(qtNorm)
    )
      hitScore = 60;
    else if (descTokens.includes(qtNorm)) hitScore = isShortNumeric ? 20 : 50;
    else if (allHaystackTokens.includes(qtNorm))
      hitScore = isShortNumeric ? 12 : 30;
    else {
      let aliasHit = false;
      for (const [canonical, aliases] of Object.entries(ALIASES)) {
        const allForms = [canonical, ...aliases].map(normalise);
        if (allForms.includes(qtNorm)) {
          if (
            allForms.some(
              (f) => allHaystackTokens.includes(f) || allHaystack.includes(f),
            )
          ) {
            hitScore = 20;
            aliasHit = true;
            break;
          }
        }
      }
      if (
        !aliasHit &&
        !isShortNumeric &&
        !isFractionToken &&
        qtNorm.length >= 3 &&
        allHaystack.includes(qtNorm)
      )
        hitScore = 5;
    }

    if (hitScore > 0) {
      score += hitScore;
      matched.push(qtNorm);
    } else {
      unmatched.push(qtNorm);
    }
  }

  // ── Unmatched token penalties ──────────────────────────────────────────────
  const intentMatchedWell = matched.some((m) => m.includes(":"));
  for (const ut of unmatched) {
    const isNumeric = /^\d+$/.test(ut);
    const isFraction = /_/.test(ut) || /\d+[-\/]\d/.test(ut);
    const base = isNumeric ? 10 : isFraction ? 25 : 25;
    score -= intentMatchedWell ? base * 0.4 : base;
  }

  // ── Bonuses ────────────────────────────────────────────────────────────────
  if (matched.length === uniqueQueryTokens.length) score += 30;

  const contentTokens = uniqueQueryTokens.filter((t) => !/^\d+$/.test(t));
  if (
    contentTokens.length > 0 &&
    contentTokens.every(
      (t) => descTokens.includes(t) || matchedCompoundTokens.has(t),
    )
  )
    score += 40;

  return { score, matched };
}

// ── Highlight matched text ─────────────────────────────────────────────────

function Highlight({ text = "", tokens = [] }) {
  if (!tokens.length) return <span>{text}</span>;
  const lower = text.toLowerCase();
  const ranges = [];
  for (const t of tokens) {
    const tn = normalise(t);
    let idx = 0;
    while ((idx = lower.indexOf(tn, idx)) !== -1) {
      ranges.push([idx, idx + tn.length]);
      idx += tn.length;
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [s, e] of ranges) {
    if (merged.length && s <= merged[merged.length - 1][1])
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    else merged.push([s, e]);
  }
  const parts = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (s > cursor) parts.push(text.slice(cursor, s));
    parts.push(
      <mark
        key={s}
        className="bg-orange-500/30 text-orange-200 rounded-sm px-0.5"
      >
        {text.slice(s, e)}
      </mark>,
    );
    cursor = e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <span>{parts}</span>;
}

function ScoreBar({ score, max }) {
  const pct = Math.min(100, Math.round((score / Math.max(max, 1)) * 100));
  const color =
    pct > 70 ? "bg-green-500" : pct > 35 ? "bg-orange-400" : "bg-gray-600";
  return (
    <div className="w-10 h-1.5 bg-gray-700 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full ${color}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV / Excel column resolver & row parser
// ─────────────────────────────────────────────────────────────────────────────

const FILE_COL = {
  category: ["category", "cat"],
  section: ["section", "section #", "section#", "sec"],
  matCode: ["mat. code", "mat code", "material code", "matcode"],
  laborCode: ["labor code", "labour code", "laborcode"],
  description: ["description", "desc", "item description"],
  size: ["size"],
  quantity: ["quantity", "qty"],
  reqQty: ["req qty", "req. qty", "required qty"],
  uom: ["uom", "unit", "u/m", "u.o.m"],
  unitCost: ["unit cost", "unitcost", "cost"],
  coveragePerUnit: ["coverage/unit", "coverage per unit", "cov/unit"],
  matTotal: ["mat. total", "mat total", "material total", "material cost"],
  laborTotal: ["labor total", "labour total"],
  itemTotal: ["item total"],
  area: ["area", "bid area"],
};

function resolveCol(headers, aliases) {
  for (const alias of aliases) {
    const found = headers.find(
      (h) => h.trim().toLowerCase() === alias.toLowerCase(),
    );
    if (found) return found;
  }
  return null;
}

function buildColMap(headers) {
  const map = {};
  for (const [key, aliases] of Object.entries(FILE_COL))
    map[key] = resolveCol(headers, aliases);
  return map;
}

function getCell(row, colName) {
  if (!colName) return "";
  return (row[colName] ?? "").toString().trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// Bid-area → PO/WO auto-mapper
// ─────────────────────────────────────────────────────────────────────────────

function resolveMappings(
  category,
  bidArea,
  purchaseOrders = [],
  workOrders = [],
) {
  const ba = (bidArea || "").toLowerCase().trim();
  if (!ba) return { mappedPO3: null, mappedWO3: null, mappingConfirmed: false };

  if (category === "L") {
    const wo =
      workOrders.find((w) => (w.bidArea || "").toLowerCase().trim() === ba) ||
      workOrders.find((w) => (w.title || "").toLowerCase().includes(ba));
    return {
      mappedPO3: null,
      mappedWO3: wo?._id?.toString() || null,
      mappingConfirmed: !!wo,
    };
  }

  const po =
    purchaseOrders.find((p) => (p.bidArea || "").toLowerCase().trim() === ba) ||
    purchaseOrders.find((p) => (p.title || "").toLowerCase().includes(ba));
  return {
    mappedPO3: po?._id?.toString() || null,
    mappedWO3: null,
    mappingConfirmed: !!po,
  };
}

function parseRows(
  rawRows,
  databaseItems,
  purchaseOrders = [],
  workOrders = [],
  sheetName = "",
) {
  if (!rawRows || rawRows.length === 0) return { valid: [], errors: [] };

  const cm = buildColMap(Object.keys(rawRows[0]));
  const dbMap = new Map();
  (databaseItems || []).forEach((i) => {
    if (i.itemCode) dbMap.set(i.itemCode.toLowerCase().trim(), i);
  });

  const ungrouped = [],
    errors = [];

  rawRows.forEach((row, idx) => {
    const cat = getCell(row, cm.category).toUpperCase();
    if (cat !== "M" && cat !== "L") return;

    const code =
      cat === "M" ? getCell(row, cm.matCode) : getCell(row, cm.laborCode);
    if (!code) return;

    const dbItem = dbMap.get(code.toLowerCase());
    if (!dbItem) {
      errors.push({ code, row: idx + 2, reason: "Not found in item database" });
      return;
    }

    const rawQty = parseFloat(getCell(row, cm.quantity)) || 0;
    const rawReqQty = parseFloat(getCell(row, cm.reqQty));
    const sign = rawQty < 0 ? -1 : 1;
    const absMag = Number.isFinite(rawReqQty)
      ? Math.abs(rawReqQty)
      : Math.abs(rawQty);
    const deltaQty = sign * absMag;
    const absQty = absMag;

    const csvUnitCost = parseFloat(
      (getCell(row, cm.unitCost) || "").replace(/[$,]/g, "").trim(),
    );
    const unitCost = Number.isFinite(csvUnitCost)
      ? csvUnitCost
      : dbItem.defaultCost || 0;

    const coveragePerUnit =
      parseFloat(getCell(row, cm.coveragePerUnit)) ||
      parseFloat(dbItem.coveragePerUnit) ||
      null;

    const parseTotal = (colKey) => {
      const raw = (getCell(row, colKey) || "").replace(/[$,\s]/g, "");
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : null;
    };

    let amount;
    if (cat === "M") {
      const fileMatTotal = parseTotal(cm.matTotal);
      if (fileMatTotal !== null) {
        amount = fileMatTotal;
      } else {
        const cov = isCovUom(dbItem.uom) ? coveragePerUnit || 1 : 1;
        amount = sign * absQty * cov * unitCost;
      }
    } else {
      const fileLabourTotal = parseTotal(cm.laborTotal);
      if (fileLabourTotal !== null) {
        amount = fileLabourTotal;
      } else {
        amount = sign * absQty * unitCost;
      }
    }

    const bidArea = getCell(row, cm.area) || dbItem.bidArea || "";
    const size = getCell(row, cm.size) || dbItem.size || "";

    const sectionNumber =
      getCell(row, cm.section) ||
      (dbItem.sectionNumber || "").trim() ||
      (dbItem.costCode?.code || "").trim() ||
      (typeof dbItem.costCode === "string" ? dbItem.costCode.trim() : "") ||
      "";

    const mapping = resolveMappings(cat, bidArea, purchaseOrders, workOrders);

    ungrouped.push({
      _rowIdx: idx,
      _localId: `csv-${Date.now()}-${idx}`,
      itemDatabaseId: dbItem._id,
      itemCode: code,
      matCode: cat === "M" ? code : "",
      laborCode: cat === "L" ? code : "",
      category: cat,
      costCodeDescription: dbItem.description || "",
      itemDescription: dbItem.description || "",
      customerDescription:
        getCell(row, cm.description) || dbItem.description || "",
      size,
      uom: dbItem.uom || "EA",
      sectionNumber,
      coveragePerUnit:
        coveragePerUnit || parseFloat(dbItem.coveragePerUnit) || null,
      unitCost: cat === "M" ? unitCost : 0,
      unitRate: cat === "L" ? unitCost : 0,
      deltaQty,
      quantity: absQty,
      amount,
      bidArea,
      _suggestedSection: sectionNumber,
      mappedPO3: mapping.mappedPO3,
      mappedWO3: mapping.mappedWO3,
      mappingConfirmed: mapping.mappingConfirmed,
      referenceNumber: null,
      includeInBreakdown: false,
      breakdownQty: absQty,
      breakdownUnitPrice: 0,
      breakdownDescription: dbItem.description || "",
      breakdownUnit: dbItem.uom || "EA",
      forBudget: true,
      _sheetName: sheetName,
    });
  });

  const grouped = new Map();
  ungrouped.forEach((item) => {
    const unitCostVal = item.category === "L" ? item.unitRate : item.unitCost;
    const sign = item.deltaQty < 0 ? "-" : "+";
    const key = [
      item.category,
      item.itemCode,
      (item.bidArea || "").trim().toLowerCase(),
      (item.size || "").trim().toLowerCase(),
      unitCostVal,
      sign,
    ].join("|");

    if (grouped.has(key)) {
      const ex = grouped.get(key);
      ex.deltaQty = parseFloat((ex.deltaQty + item.deltaQty).toFixed(4));
      ex.quantity = Math.abs(ex.deltaQty);
      ex.amount = parseFloat((ex.amount + item.amount).toFixed(2));
      ex.breakdownQty = ex.quantity;
    } else {
      grouped.set(key, { ...item });
    }
  });

  return { valid: Array.from(grouped.values()), errors };
}

// ─────────────────────────────────────────────────────────────────────────────
// Import Panel (CSV / Excel)
// ─────────────────────────────────────────────────────────────────────────────

function ImportPanel({
  databaseItems,
  purchaseOrders,
  workOrders,
  onAdd,
  onClose,
  sectionNumber,
}) {
  const [parsed, setParsed] = useState([]);
  const [errors, setErrors] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const fileRef = useRef(null);
  const [isMultiSheet, setIsMultiSheet] = useState(false);

  const handleFile = useCallback(
    (file) => {
      if (!file) return;
      setFileName(file.name);
      setParsing(true);
      const ext = file.name.split(".").pop().toLowerCase();

      const process = (rawRows) => {
        const { valid, errors } = parseRows(
          rawRows,
          databaseItems,
          purchaseOrders,
          workOrders,
        );
        setParsed(valid);
        setErrors(errors);
        setSelected(new Set(valid.map((_, i) => i)));
        setParsing(false);
      };

      if (ext === "csv") {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          encoding: "latin1",
          complete: ({ data }) => process(data),
          error: (err) => {
            alert("CSV error: " + err.message);
            setParsing(false);
          },
        });
      } else if (ext === "xlsx" || ext === "xls") {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const wb = XLSX.read(e.target.result, { type: "array" });
            if (wb.SheetNames.length > 1) {
              let allValid = [],
                allErrors = [];
              wb.SheetNames.forEach((name) => {
                const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], {
                  defval: "",
                });
                const { valid, errors } = parseRows(
                  rows,
                  databaseItems,
                  purchaseOrders,
                  workOrders,
                  name,
                );
                allValid = [...allValid, ...valid];
                allErrors = [...allErrors, ...errors];
              });
              setParsed(allValid);
              setErrors(allErrors);
              setSelected(new Set(allValid.map((_, i) => i)));
              setIsMultiSheet(true);
              setParsing(false);
            } else {
              process(
                XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
                  defval: "",
                }),
              );
            }
          } catch (err) {
            alert("Excel error: " + err.message);
            setParsing(false);
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        alert("Unsupported file — use .csv, .xlsx, or .xls");
        setParsing(false);
      }
    },
    [databaseItems, purchaseOrders, workOrders],
  );

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const totalSelected = Array.from(selected).reduce(
    (sum, i) => sum + (parsed[i]?.amount || 0),
    0,
  );

  const handleAdd = () => {
    const toAdd = parsed
      .filter((_, i) => selected.has(i))
      .map((item) => ({
        ...item,
        _localId: `csv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        referenceNumber: isMultiSheet ? null : (sectionNumber ?? null),
      }));
    onAdd(toAdd);
    onClose();
  };

  if (parsed.length === 0) {
    return (
      <div className="flex flex-col flex-1 min-h-0 p-5 gap-3">
        <p className="text-xs text-gray-400 shrink-0">
          Upload a <span className="text-white font-medium">.csv</span>,{" "}
          <span className="text-white font-medium">.xlsx</span>, or{" "}
          <span className="text-white font-medium">.xls</span> export. Columns:{" "}
          <span className="text-gray-300">
            Category, Mat. Code / Labor Code, Description, Quantity, Area,
            Section, Size, Unit Cost.
          </span>{" "}
          Positive and negative quantities both supported.
        </p>
        <div
          className="flex-1 min-h-[180px] border-2 border-dashed border-gray-600 rounded-xl
                     flex flex-col items-center justify-center gap-3 cursor-pointer
                     hover:border-orange-500 transition-colors"
          onClick={() => fileRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          {parsing ? (
            <>
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-orange-500" />
              <p className="text-gray-400 text-sm">Parsing…</p>
            </>
          ) : (
            <>
              <p className="text-4xl text-gray-600">↑</p>
              <p className="text-sm text-gray-300 font-medium">
                Drop file or click to browse
              </p>
              <p className="text-xs text-gray-500">CSV · XLSX · XLS</p>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-700 shrink-0 flex-wrap">
        <span className="text-xs text-gray-400 truncate max-w-[160px]">
          {fileName}
        </span>
        <span className="text-xs text-green-400 font-medium">
          {parsed.length} valid
        </span>
        {errors.length > 0 && (
          <span className="text-xs text-red-400">{errors.length} skipped</span>
        )}
        <span className="text-xs text-blue-400">{selected.size} selected</span>
        <span
          className={cls(
            "text-xs font-medium ml-auto",
            totalSelected < 0 ? "text-red-400" : "text-green-300",
          )}
        >
          Total: {fmtC(totalSelected)}
        </span>
        <button
          onClick={() => {
            setParsed([]);
            setErrors([]);
            setSelected(new Set());
            setFileName("");
          }}
          className="text-xs text-gray-500 hover:text-gray-300 underline shrink-0"
        >
          ← New file
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs min-w-[620px]">
          <thead className="bg-gray-900 sticky top-0 z-10">
            <tr>
              <th className="px-2 py-2 w-8">
                <input
                  type="checkbox"
                  checked={selected.size === parsed.length}
                  onChange={() =>
                    setSelected(
                      selected.size === parsed.length
                        ? new Set()
                        : new Set(parsed.map((_, i) => i)),
                    )
                  }
                  className="rounded border-gray-500 bg-gray-700"
                />
              </th>
              {[
                "Cat",
                "Code",
                "Description",
                "Area",
                "PO / WO",
                "Qty",
                "Amount",
              ].map((h) => (
                <th
                  key={h}
                  className="px-2 py-2 text-left text-gray-400 font-medium"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/40">
            {isMultiSheet
              ? (() => {
                  const sheetNames = [
                    ...new Set(parsed.map((i) => i._sheetName).filter(Boolean)),
                  ];
                  return sheetNames.flatMap((sheetName) => {
                    const sheetItems = parsed
                      .map((item, i) => ({ item, i }))
                      .filter(({ item }) => item._sheetName === sheetName);
                    const allSheetSel = sheetItems.every(({ i }) =>
                      selected.has(i),
                    );
                    return [
                      <tr
                        key={`sheet-${sheetName}`}
                        className="bg-orange-900/20 border-t-2 border-orange-500/30"
                      >
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={allSheetSel}
                            onChange={() =>
                              setSelected((p) => {
                                const n = new Set(p);
                                sheetItems.forEach(({ i }) =>
                                  allSheetSel ? n.delete(i) : n.add(i),
                                );
                                return n;
                              })
                            }
                            className="rounded border-gray-500 bg-gray-700 accent-orange-500"
                          />
                        </td>
                        <td colSpan={7} className="px-2 py-1.5">
                          <span className="text-xs font-semibold text-orange-300">
                            📋 Section: {sheetName}
                          </span>
                          <span className="ml-2 text-[10px] text-gray-500">
                            ({sheetItems.length} item
                            {sheetItems.length !== 1 ? "s" : ""})
                          </span>
                        </td>
                      </tr>,
                      ...sheetItems.map(({ item, i }) => (
                        <tr
                          key={i}
                          className={cls(
                            "hover:bg-gray-700/30 cursor-pointer",
                            selected.has(i) && "bg-green-900/10",
                          )}
                          onClick={() =>
                            setSelected((p) => {
                              const n = new Set(p);
                              n.has(i) ? n.delete(i) : n.add(i);
                              return n;
                            })
                          }
                        >
                          <td
                            className="px-2 py-1.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selected.has(i)}
                              onChange={() =>
                                setSelected((p) => {
                                  const n = new Set(p);
                                  n.has(i) ? n.delete(i) : n.add(i);
                                  return n;
                                })
                              }
                              className="rounded border-gray-500 bg-gray-700"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <span
                              className={cls(
                                "inline-block px-1.5 py-0.5 text-[10px] font-bold rounded",
                                item.category === "M"
                                  ? "bg-blue-900/60 text-blue-300"
                                  : "bg-orange-900/60 text-orange-300",
                              )}
                            >
                              {item.category}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 font-mono text-gray-300">
                            {item.itemCode}
                          </td>
                          <td className="px-2 py-1.5 text-gray-200 max-w-[180px] truncate">
                            {item.customerDescription}
                          </td>
                          <td className="px-2 py-1.5 text-gray-400">
                            {item.bidArea || "—"}
                          </td>
                          <td className="px-2 py-1.5">
                            {item.mappingConfirmed ? (
                              <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-900/50 text-purple-300">
                                {item.mappedPO3
                                  ? `PO ${(purchaseOrders || []).find((p) => p._id?.toString() === item.mappedPO3)?.poNumber ?? "✓"}`
                                  : `WO ${(workOrders || []).find((w) => w._id?.toString() === item.mappedWO3)?.woNumber ?? "✓"}`}
                              </span>
                            ) : (
                              <span className="text-gray-600 text-[10px]">
                                —
                              </span>
                            )}
                          </td>
                          <td
                            className={cls(
                              "px-2 py-1.5 font-mono font-semibold text-right",
                              (item.deltaQty || 0) < 0
                                ? "text-red-400"
                                : "text-white",
                            )}
                          >
                            {(item.deltaQty || 0) > 0 ? "+" : ""}
                            {item.deltaQty}
                          </td>
                          <td
                            className={cls(
                              "px-2 py-1.5 font-mono text-right",
                              (item.amount || 0) < 0
                                ? "text-red-400"
                                : "text-green-300",
                            )}
                          >
                            {fmtC(item.amount || 0)}
                          </td>
                        </tr>
                      )),
                    ];
                  });
                })()
              : parsed.map((item, i) => (
                  <tr
                    key={i}
                    className={cls(
                      "hover:bg-gray-700/30 cursor-pointer",
                      selected.has(i) && "bg-green-900/10",
                    )}
                    onClick={() =>
                      setSelected((p) => {
                        const n = new Set(p);
                        n.has(i) ? n.delete(i) : n.add(i);
                        return n;
                      })
                    }
                  >
                    <td
                      className="px-2 py-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(i)}
                        onChange={() =>
                          setSelected((p) => {
                            const n = new Set(p);
                            n.has(i) ? n.delete(i) : n.add(i);
                            return n;
                          })
                        }
                        className="rounded border-gray-500 bg-gray-700"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <span
                        className={cls(
                          "inline-block px-1.5 py-0.5 text-[10px] font-bold rounded",
                          item.category === "M"
                            ? "bg-blue-900/60 text-blue-300"
                            : "bg-orange-900/60 text-orange-300",
                        )}
                      >
                        {item.category}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-gray-300">
                      {item.itemCode}
                    </td>
                    <td className="px-2 py-1.5 text-gray-200 max-w-[180px] truncate">
                      {item.customerDescription}
                    </td>
                    <td className="px-2 py-1.5 text-gray-400">
                      {item.bidArea || "—"}
                    </td>
                    <td className="px-2 py-1.5">
                      {item.mappingConfirmed ? (
                        <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-900/50 text-purple-300">
                          {item.mappedPO3
                            ? `PO ${(purchaseOrders || []).find((p) => p._id?.toString() === item.mappedPO3)?.poNumber ?? "✓"}`
                            : `WO ${(workOrders || []).find((w) => w._id?.toString() === item.mappedWO3)?.woNumber ?? "✓"}`}
                        </span>
                      ) : (
                        <span className="text-gray-600 text-[10px]">—</span>
                      )}
                    </td>
                    <td
                      className={cls(
                        "px-2 py-1.5 font-mono font-semibold text-right",
                        (item.deltaQty || 0) < 0
                          ? "text-red-400"
                          : "text-white",
                      )}
                    >
                      {(item.deltaQty || 0) > 0 ? "+" : ""}
                      {item.deltaQty}
                    </td>
                    <td
                      className={cls(
                        "px-2 py-1.5 font-mono text-right",
                        (item.amount || 0) < 0
                          ? "text-red-400"
                          : "text-green-300",
                      )}
                    >
                      {fmtC(item.amount || 0)}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {errors.length > 0 && (
        <div className="shrink-0 border-t border-gray-700 max-h-24 overflow-y-auto bg-red-900/5">
          <p className="px-4 py-1 text-[10px] text-red-400 font-medium">
            ⚠ {errors.length} row{errors.length !== 1 ? "s" : ""} skipped (code
            not in database)
          </p>
          {errors.map((e, i) => (
            <div
              key={i}
              className="px-4 pb-0.5 text-[10px] text-gray-500 flex gap-3"
            >
              <span className="text-gray-600">Row {e.row}</span>
              <span className="font-mono text-gray-400">{e.code}</span>
              <span>{e.reason}</span>
            </div>
          ))}
        </div>
      )}

      <div className="shrink-0 border-t border-gray-700 px-4 py-3 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-3 py-1.5 text-xs text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          onClick={handleAdd}
          disabled={selected.size === 0}
          className="px-4 py-1.5 text-xs font-semibold text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-40"
        >
          Add {selected.size} Item{selected.size !== 1 ? "s" : ""} to Section
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cost resolver
// ─────────────────────────────────────────────────────────────────────────────

function resolveDbItemCost(dbItem) {
  if (dbItem.category === "L") {
    return dbItem.defaultUnitRate || dbItem.defaultCost || 0;
  }
  const vendorPrices = dbItem.vendorPrices || [];
  const defaultVendorId =
    dbItem.defaultVendor?._id?.toString() ||
    (typeof dbItem.defaultVendor === "string" ? dbItem.defaultVendor : null);

  if (defaultVendorId) {
    const entry = vendorPrices.find(
      (vp) =>
        (vp.vendor?._id?.toString() || vp.vendor?.toString()) ===
        defaultVendorId,
    );
    if (entry) return entry.price || entry.unitRate || 0;
  }
  if (vendorPrices.length > 0) {
    return vendorPrices[0].price || vendorPrices[0].unitRate || 0;
  }
  return dbItem.defaultCost || 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Database Panel — upgraded search engine with AI intent + token pills
// ─────────────────────────────────────────────────────────────────────────────

function DatabasePanel({
  databaseItems,
  sectionNumber,
  onAdd,
  onClose,
  token,
}) {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [selected, setSelected] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [intent, setIntent] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const inputRef = useRef(null);
  const rowRefs = useRef({});

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 60);
  }, []);

  // AI intent extraction (debounced, only for steel-framing-like queries)
  useEffect(() => {
    if (!query.trim() || query.trim().length < 3) {
      setIntent(null);
      return;
    }
    const timer = setTimeout(async () => {
      setIntentLoading(true);
      const result = await extractSearchIntent(query, token);
      setIntent(result);
      setIntentLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [query, token]);

  const queryTokens = useMemo(() => tokenise(query), [query]);

  const scored = useMemo(() => {
    let list = databaseItems || [];
    if (cat !== "all") list = list.filter((i) => i.category === cat);

    if (!queryTokens.length) {
      return list
        .map((item) => ({ item, score: 0, matched: [] }))
        .sort((a, b) =>
          (a.item.itemCode || "").localeCompare(b.item.itemCode || ""),
        );
    }

    return list
      .map((item) => ({ item, ...scoreItem(item, queryTokens, query, intent) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score);
  }, [databaseItems, cat, queryTokens, query, intent]);

  const maxScore = useMemo(
    () => Math.max(...scored.map((r) => r.score), 1),
    [scored],
  );

  useEffect(() => setActiveIdx(0), [scored]);

  const toggle = (item) =>
    setSelected((p) =>
      p.find((s) => s.itemCode === item.itemCode)
        ? p.filter((s) => s.itemCode !== item.itemCode)
        : [...p, item],
    );

  const handleAdd = () => {
    const items = selected.map((dbItem) => {
      const unitCost = resolveDbItemCost(dbItem);

      let resolvedCoverage = parseFloat(dbItem.coveragePerUnit) || null;
      if (!resolvedCoverage && dbItem.size && isCovUom(dbItem.uom)) {
        const computed = computeCovFromSize(dbItem.size, dbItem.uom);
        if (computed) resolvedCoverage = computed;
      }

      const cov =
        dbItem.category === "M" && isCovUom(dbItem.uom)
          ? resolvedCoverage || 1
          : 1;

      return {
        _localId: `db-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        itemDatabaseId: dbItem._id,
        itemCode: dbItem.itemCode,
        matCode: dbItem.category === "M" ? dbItem.itemCode : "",
        laborCode: dbItem.category === "L" ? dbItem.itemCode : "",
        category: dbItem.category,
        costCodeDescription: dbItem.description || "",
        itemDescription: dbItem.description || "",
        customerDescription: dbItem.description || "",
        size: dbItem.size || "",
        uom: dbItem.uom || "EA",
        sectionNumber: dbItem.sectionNumber || dbItem.costCode?.code || "",
        coveragePerUnit: resolvedCoverage,
        unitCost: dbItem.category === "M" ? unitCost : 0,
        unitRate: dbItem.category === "L" ? unitCost : 0,
        deltaQty: 1,
        quantity: 1,
        amount: unitCost * cov,
        bidArea: dbItem.bidArea || "",
        mappedPO3: null,
        mappedWO3: null,
        mappingConfirmed: false,
        referenceNumber: sectionNumber ?? null,
        includeInBreakdown: false,
        breakdownQty: 1,
        breakdownUnitPrice: 0,
        breakdownDescription: dbItem.description || "",
        breakdownUnit: dbItem.uom || "EA",
        forBudget: true,
      };
    });
    onAdd(items);
    setSelected([]);
    setQuery("");
    onClose();
  };

  const scrollTo = (idx) =>
    rowRefs.current[idx]?.scrollIntoView({ block: "nearest" });

  const handleKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => {
        const n = Math.min(i + 1, scored.length - 1);
        scrollTo(n);
        return n;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => {
        const n = Math.max(i - 1, 0);
        scrollTo(n);
        return n;
      });
    } else if (e.key === "Enter" && scored[activeIdx]) {
      toggle(scored[activeIdx].item);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0" onKeyDown={handleKey}>
      {/* Search bar */}
      <div className="px-4 pt-3 pb-2 border-b border-gray-700 shrink-0 space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code, description, size, gauge, section…"
              className="w-full pl-9 pr-8 py-2 text-xs bg-gray-700 border border-gray-600 rounded-lg
                         text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-sm"
              >
                ×
              </button>
            )}
          </div>
          <div className="flex rounded-lg overflow-hidden border border-gray-600 shrink-0">
            {["all", "M", "L"].map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={cls(
                  "px-3 py-1.5 text-xs font-medium transition-colors",
                  cat === c
                    ? "bg-orange-500 text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600",
                )}
              >
                {c === "all" ? "All" : c === "M" ? "Material" : "Labour"}
              </button>
            ))}
          </div>
        </div>

        {/* Token pills + intent indicator + count */}
        <div className="flex items-center justify-between min-h-[18px]">
          <div className="flex items-center gap-1.5 flex-wrap">
            {queryTokens.length > 0 && (
              <>
                <span className="text-[10px] text-gray-600">Tokens:</span>
                {queryTokens.map((t) => (
                  <span
                    key={t}
                    className="px-1.5 py-0.5 text-[9px] font-mono bg-gray-800 text-gray-400 rounded border border-gray-700"
                  >
                    {t}
                  </span>
                ))}
              </>
            )}
            {intentLoading && (
              <span className="text-[9px] text-purple-500 animate-pulse ml-1">
                AI parsing…
              </span>
            )}
            {intent && !intentLoading && (
              <span className="text-[9px] text-purple-400 ml-1 border border-purple-700 rounded px-1 py-0.5 bg-purple-900/20">
                ✦ AI:{" "}
                {[
                  intent.productType,
                  intent.webSize,
                  intent.gauge && `${intent.gauge}ga`,
                  intent.flange,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            )}
          </div>
          <span className="text-[10px] text-gray-600 shrink-0">
            {scored.length} of {(databaseItems || []).length} · ↑↓ navigate · ↵
            select
          </span>
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-900 sticky top-0 z-10 border-b border-gray-700/70">
            <tr>
              <th className="px-2 py-2.5 w-8" />
              <th className="px-2 py-2.5 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wide w-10">
                Cat
              </th>
              <th className="px-2 py-2.5 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wide w-28">
                Code
              </th>
              <th className="px-2 py-2.5 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wide">
                Description
              </th>
              <th className="px-2 py-2.5 text-center text-[10px] text-gray-500 font-medium uppercase tracking-wide w-16">
                UOM
              </th>
              <th className="px-2 py-2.5 text-right text-[10px] text-gray-500 font-medium uppercase tracking-wide w-24">
                Unit Cost
              </th>
              <th className="px-2 py-2.5 w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {scored.map(({ item, score, matched }, idx) => {
              const isSel = selected.some((s) => s.itemCode === item.itemCode);
              const isActive = idx === activeIdx;
              return (
                <tr
                  key={item.itemCode || idx}
                  ref={(el) => {
                    rowRefs.current[idx] = el;
                  }}
                  onMouseEnter={() => setActiveIdx(idx)}
                  onClick={() => toggle(item)}
                  className={cls(
                    "cursor-pointer transition-colors group",
                    isSel
                      ? "bg-blue-900/25 hover:bg-blue-900/35"
                      : isActive
                        ? "bg-orange-500/10 border-l-2 border-l-orange-500"
                        : idx % 2 === 0
                          ? "bg-gray-900/40 hover:bg-gray-800/60"
                          : "bg-gray-800/20 hover:bg-gray-800/60",
                  )}
                >
                  <td
                    className="px-2 py-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(item)}
                      className="rounded border-gray-500 bg-gray-700 w-4 h-4"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <span
                      className={cls(
                        "inline-block px-1.5 py-0.5 text-[10px] font-bold rounded",
                        item.category === "M"
                          ? "bg-blue-900/60 text-blue-300"
                          : "bg-orange-900/60 text-orange-300",
                      )}
                    >
                      {item.category}
                    </span>
                  </td>
                  <td className="px-2 py-2 font-mono">
                    <span
                      className={cls(
                        "text-[11px] font-medium",
                        isActive ? "text-orange-300" : "text-gray-300",
                      )}
                    >
                      <Highlight
                        text={item.itemCode || "—"}
                        tokens={queryTokens}
                      />
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <p className="text-gray-100 text-[12px] leading-snug font-medium">
                      <Highlight
                        text={
                          item.description ||
                          item.costCodeDescription ||
                          item.itemDescription ||
                          "—"
                        }
                        tokens={queryTokens}
                      />
                    </p>
                    {item.size && (
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        <Highlight text={item.size} tokens={queryTokens} />
                        {item.uom && (
                          <span className="ml-1 text-gray-600">{item.uom}</span>
                        )}
                      </p>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-400">
                      {item.uom || "—"}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-gray-300">
                    {fmtC(resolveDbItemCost(item))}
                  </td>
                  {/* Score bar */}
                  <td className="px-2 py-2">
                    {query && <ScoreBar score={score} max={maxScore} />}
                  </td>
                </tr>
              );
            })}
            {scored.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="py-12 text-center text-gray-500 text-sm"
                >
                  {query ? (
                    <span>
                      No items match "{query}"{" "}
                      <button
                        onClick={() => setQuery("")}
                        className="text-orange-400 hover:text-orange-300 underline ml-1"
                      >
                        clear
                      </button>
                    </span>
                  ) : (
                    "No items available"
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="shrink-0 border-t border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4 text-[10px] text-gray-600">
          {[
            ["↑↓", "navigate"],
            ["↵", "select"],
            ["Esc", "close"],
          ].map(([key, label]) => (
            <span key={key}>
              <kbd className="px-1 py-0.5 bg-gray-800 border border-gray-700 rounded font-mono text-[9px] text-gray-500">
                {key}
              </kbd>{" "}
              {label}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {selected.length > 0 && (
            <span className="text-xs text-blue-400 font-medium">
              {selected.length} selected
            </span>
          )}
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={selected.length === 0}
            className="px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40"
          >
            Add {selected.length > 0 ? selected.length : ""} Item
            {selected.length !== 1 ? "s" : ""} to Section
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// T&M Panel (unchanged)
// ─────────────────────────────────────────────────────────────────────────────

function TandMPanel({ projectId, token, databaseItems = [], onAdd, onClose }) {
  const [tandms, setTandms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (!projectId || !token) return;
    setLoading(true);
    fetch(`${API_BASE}/changeOrders3/project/${projectId}/eligible-tandms`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setTandms(data.tandms || []);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load T&M entries.");
        setLoading(false);
      });
  }, [projectId, token]);

  const sortedTandms = useMemo(
    () =>
      [...tandms].sort(
        (a, b) =>
          parseInt(b.timeAndMaterialNumber) - parseInt(a.timeAndMaterialNumber),
      ),
    [tandms],
  );

  const filteredTandms = useMemo(() => {
    if (statusFilter === "all") return sortedTandms;
    return sortedTandms
      .map((t) => ({
        ...t,
        scopes: (t.scopes || []).filter((s) =>
          statusFilter === "imported"
            ? s.convertedToChangeOrder
            : !s.convertedToChangeOrder,
        ),
      }))
      .filter((t) => t.scopes.length > 0);
  }, [sortedTandms, statusFilter]);

  const counts = useMemo(() => {
    let newCount = 0,
      importedCount = 0;
    tandms.forEach((t) =>
      (t.scopes || []).forEach((s) => {
        if (s.convertedToChangeOrder) importedCount++;
        else newCount++;
      }),
    );
    return { newCount, importedCount, total: newCount + importedCount };
  }, [tandms]);

  const allScopes = filteredTandms.flatMap((t) =>
    (t.scopes || []).map((s) => ({ ...s, _tandm: t })),
  );

  const toggleScope = (scopeId) =>
    setSelected((p) => {
      const n = new Set(p);
      n.has(scopeId) ? n.delete(scopeId) : n.add(scopeId);
      return n;
    });

  const toggleTandM = (tandm) => {
    const ids = (tandm.scopes || []).map((s) => s._id);
    const allSel = ids.every((id) => selected.has(id));
    setSelected((p) => {
      const n = new Set(p);
      ids.forEach((id) => (allSel ? n.delete(id) : n.add(id)));
      return n;
    });
  };

  const handleAdd = () => {
    const items = [];

    const dbMap = new Map();
    (databaseItems || []).forEach((d) => {
      if (d.itemCode) dbMap.set(d.itemCode.trim().toLowerCase(), d);
    });

    const allScopesUnfiltered = tandms.flatMap((t) =>
      (t.scopes || []).map((s) => ({ ...s, _tandm: t })),
    );

    const scopeRefs = [];

    for (const { _tandm, ...scope } of allScopesUnfiltered) {
      if (!selected.has(scope._id)) continue;

      scopeRefs.push({ tandmId: _tandm._id, scopeId: scope._id });

      const sheetName =
        scope.name?.trim() ||
        `T&M #${_tandm.timeAndMaterialNumber}${
          scope.scopeNumber != null
            ? ` – Entry ${String(scope.scopeNumber).padStart(2, "0")}`
            : ""
        }`;

      for (const item of scope.items || []) {
        const cat = (item.category || "").trim().toUpperCase();
        if (cat !== "M" && cat !== "L") continue;

        const dbItem = dbMap.get((item.itemCode || "").trim().toLowerCase());

        const unitCost = cat === "M" ? resolveDbItemCost(dbItem || {}) : 0;
        const unitRate =
          cat === "L" ? dbItem?.defaultUnitRate || dbItem?.defaultCost || 0 : 0;

        const coveragePerUnit = (() => {
          const dbCov = parseFloat(dbItem?.coveragePerUnit);
          if (Number.isFinite(dbCov) && dbCov > 0) return dbCov;
          if (cat === "M" && item.size && isCovUom(item.uom || "")) {
            const computed = computeCovFromSize(item.size, item.uom);
            if (computed) return computed;
          }
          return null;
        })();

        const qty =
          parseFloat(item.qty != null ? item.qty : item.quantity) || 0;
        const absQty = Math.abs(qty);
        const cov =
          cat === "M" && isCovUom(item.uom || "") ? coveragePerUnit || 1 : 1;
        const costBase = cat === "L" ? unitRate : unitCost;
        const amount =
          qty < 0 ? -(absQty * cov * costBase) : absQty * cov * costBase;

        const sectionNumber =
          item.sectionNumber ||
          (dbItem?.sectionNumber || "").trim() ||
          (dbItem?.costCode?.code || "").trim() ||
          (typeof dbItem?.costCode === "string"
            ? dbItem.costCode.trim()
            : "") ||
          "";

        items.push({
          _localId: `tandm-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 7)}`,
          _sheetName: sheetName,
          itemDatabaseId: dbItem?._id || null,
          itemCode: (item.itemCode || "").trim(),
          matCode: cat === "M" ? (item.itemCode || "").trim() : "",
          laborCode: cat === "L" ? (item.itemCode || "").trim() : "",
          category: cat,
          costCodeDescription: dbItem?.description || item.description || "",
          itemDescription: dbItem?.description || item.description || "",
          customerDescription: item.notes
            ? `${item.description || item.itemCode} — ${item.notes}`
            : item.description || dbItem?.description || "",
          size: item.size || dbItem?.size || "",
          uom: dbItem?.uom || item.uom || "EA",
          sectionNumber,
          coveragePerUnit,
          unitCost,
          unitRate,
          deltaQty: qty,
          quantity: absQty,
          amount,
          bidArea: item.bidArea || "",
          mappedPO3: null,
          mappedWO3: null,
          mappingConfirmed: false,
          referenceNumber: null,
          includeInBreakdown: false,
          breakdownQty: absQty,
          breakdownUnitPrice: 0,
          breakdownDescription: item.notes
            ? `${item.description || item.itemCode} — ${item.notes}`
            : item.description || dbItem?.description || "",
          breakdownUnit: dbItem?.uom || item.uom || "EA",
          forBudget: true,
          _priceFound: !!(cat === "L" ? unitRate : unitCost),
        });
      }
    }

    onAdd(items, scopeRefs);
    onClose();
  };

  if (loading)
    return (
      <div className="flex items-center justify-center flex-1 gap-3 py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-700 border-t-purple-500" />
        <span className="text-gray-400 text-sm">Loading T&M entries…</span>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center flex-1 py-16">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );

  if (tandms.length === 0)
    return (
      <div className="flex flex-col items-center justify-center flex-1 py-16 gap-2">
        <p className="text-3xl">📋</p>
        <p className="text-sm text-gray-400 font-medium">
          No approved T&M scopes ready for CO
        </p>
        <p className="text-xs text-gray-600">
          Approve scopes in the T&M tab first.
        </p>
      </div>
    );

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Filter bar */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-700 shrink-0">
        <span className="text-xs text-gray-500 mr-1">Show:</span>
        {[
          { id: "all", label: "All", count: counts.total },
          { id: "new", label: "Not Imported", count: counts.newCount },
          {
            id: "imported",
            label: "Previously Imported",
            count: counts.importedCount,
          },
        ].map(({ id, label, count }) => (
          <button
            key={id}
            onClick={() => setStatusFilter(id)}
            className={cls(
              "flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors",
              statusFilter === id
                ? id === "imported"
                  ? "bg-yellow-900/40 border-yellow-600/50 text-yellow-300"
                  : id === "new"
                    ? "bg-green-900/40 border-green-600/50 text-green-300"
                    : "bg-purple-900/40 border-purple-600/50 text-purple-300"
                : "bg-gray-700 border-gray-600 text-gray-400 hover:text-gray-200",
            )}
          >
            {label}
            <span
              className={cls(
                "text-[10px] font-bold px-1 py-0.5 rounded-full",
                statusFilter === id ? "bg-white/10" : "bg-gray-600",
              )}
            >
              {count}
            </span>
          </button>
        ))}
        <span className="ml-auto text-[10px] text-gray-600">Newest first</span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-auto">
        {filteredTandms.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <p className="text-2xl">🔍</p>
            <p className="text-sm text-gray-400">
              No {statusFilter === "imported" ? "previously imported" : "new"}{" "}
              scopes
            </p>
          </div>
        ) : (
          filteredTandms.map((tandm) => {
            const scopes = tandm.scopes || [];
            const allSel = scopes.every((s) => selected.has(s._id));
            const someSel = scopes.some((s) => selected.has(s._id));
            return (
              <div key={tandm._id} className="border-b border-gray-700/50">
                <div
                  className="flex items-center gap-3 px-4 py-2.5 bg-gray-900/60 cursor-pointer hover:bg-gray-700/40 transition-colors"
                  onClick={() => toggleTandM(tandm)}
                >
                  <input
                    type="checkbox"
                    checked={allSel}
                    ref={(el) => {
                      if (el) el.indeterminate = someSel && !allSel;
                    }}
                    onChange={() => toggleTandM(tandm)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-500 bg-gray-700 accent-purple-500 w-4 h-4"
                  />
                  <span className="text-xs font-bold text-orange-400 font-mono">
                    T&M #{tandm.timeAndMaterialNumber}
                  </span>
                  <span className="text-xs text-gray-300 font-medium flex-1 truncate">
                    {tandm.scopeOfWork}
                  </span>
                  {tandm.date && (
                    <span className="text-[10px] text-gray-500 shrink-0">
                      {new Date(tandm.date).toLocaleDateString()}
                    </span>
                  )}
                  <span className="text-[10px] text-purple-400 shrink-0">
                    {scopes.length} scope{scopes.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {scopes.map((scope) => {
                  const isSel = selected.has(scope._id);
                  const mCount = (scope.items || []).filter(
                    (i) => i.category === "M",
                  ).length;
                  const lCount = (scope.items || []).filter(
                    (i) => i.category === "L",
                  ).length;
                  const alreadyImported = !!scope.convertedToChangeOrder;
                  const linkedCO = scope._linkedCO;
                  return (
                    <div
                      key={scope._id}
                      onClick={() => toggleScope(scope._id)}
                      className={cls(
                        "flex items-center gap-3 px-6 py-2 cursor-pointer transition-colors border-t border-gray-700/30",
                        isSel
                          ? "bg-purple-900/20 hover:bg-purple-900/30"
                          : alreadyImported
                            ? "hover:bg-yellow-900/10"
                            : "hover:bg-gray-700/30",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleScope(scope._id)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-gray-500 bg-gray-700 accent-purple-500 w-4 h-4"
                      />
                      {scope.scopeNumber != null && (
                        <span className="text-[10px] font-mono font-bold text-gray-500 shrink-0">
                          #{String(scope.scopeNumber).padStart(2, "0")}
                        </span>
                      )}
                      <span className="text-xs text-gray-200 font-medium flex-1 truncate">
                        {scope.name}
                      </span>
                      {scope.note && (
                        <span className="text-[10px] text-gray-500 truncate max-w-[120px]">
                          {scope.note}
                        </span>
                      )}
                      <div className="flex items-center gap-1.5 shrink-0">
                        {alreadyImported && (
                          <span
                            title={
                              linkedCO
                                ? `Imported to CO3-${linkedCO.number}${linkedCO.title ? ` — ${linkedCO.title}` : ""}`
                                : "Previously imported to a CO"
                            }
                            className={cls(
                              "text-[10px] px-1.5 py-0.5 rounded border font-medium",
                              isSel
                                ? "bg-yellow-900/60 text-yellow-300 border-yellow-700"
                                : "bg-gray-700 text-gray-400 border-gray-600",
                            )}
                          >
                            {linkedCO ? `→ CO3-${linkedCO.number}` : "imported"}
                          </span>
                        )}
                        {mCount > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/60 text-blue-300 rounded">
                            {mCount}M
                          </span>
                        )}
                        {lCount > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-yellow-900/60 text-yellow-300 rounded">
                            {lCount}L
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 border-t border-gray-700 px-4 py-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {selected.size > 0 && (
            <span className="text-purple-400 font-medium">
              {selected.size} scope{selected.size !== 1 ? "s" : ""} selected
            </span>
          )}
        </span>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={selected.size === 0}
            className="px-4 py-1.5 text-xs font-semibold text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-40"
          >
            Import {selected.size > 0 ? selected.size : ""} Scope
            {selected.size !== 1 ? "s" : ""} to CO
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

export default function COItemSearchHelper({
  open,
  onClose,
  databaseItems = [],
  purchaseOrders = [],
  workOrders = [],
  sectionNumber,
  knownBidAreas = [],
  onAdd,
  projectId,
  token,
}) {
  const [tab, setTab] = useState(TAB_DB);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70">
      <div
        className="bg-gray-800 rounded-xl border border-gray-700 shadow-2xl flex flex-col w-full max-w-7xl mx-4"
        style={{ height: "85vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-700 shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-white">
              Add Items to Section
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Items will be linked to this pricing section
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-gray-700 px-5 shrink-0">
          {[
            [TAB_DB, "📦 From Database", "border-blue-500 text-blue-400"],
            [
              TAB_CSV,
              "↑ Import CSV / Excel",
              "border-orange-500 text-orange-400",
            ],
            [TAB_TANDM, "📋 From T&M", "border-purple-500 text-purple-400"],
          ].map(([id, label, activeClass]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={cls(
                "px-4 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px",
                tab === id
                  ? activeClass
                  : "border-transparent text-gray-400 hover:text-gray-200",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {tab === TAB_DB ? (
            <DatabasePanel
              databaseItems={databaseItems}
              sectionNumber={sectionNumber}
              onAdd={onAdd}
              onClose={onClose}
              token={token}
            />
          ) : tab === TAB_CSV ? (
            <ImportPanel
              databaseItems={databaseItems}
              purchaseOrders={purchaseOrders}
              workOrders={workOrders}
              sectionNumber={sectionNumber}
              onAdd={onAdd}
              onClose={onClose}
            />
          ) : (
            <TandMPanel
              projectId={projectId}
              token={token}
              databaseItems={databaseItems}
              onAdd={(items, scopeRefs) => {
                onAdd(items, scopeRefs);
              }}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}
